import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Chip, Box, IconButton,
  Typography, Alert, Tabs, Tab
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import * as articleApi from '../../services/article';
import * as industryApi from '../../services/industry';

interface ArticleFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  type: 'article' | 'innovation';
}

const ArticleForm = ({ open, onClose, onSuccess, type }: ArticleFormProps) => {
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [industry, setIndustry] = useState('');
  const [industryId, setIndustryId] = useState('');
  const [content, setContent] = useState<string[]>(['']);
  const [keyPoints, setKeyPoints] = useState<string[]>(['']);
  const [conclusion, setConclusion] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [industries, setIndustries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const data = await industryApi.getAllIndustries();
        setIndustries(data);
      } catch (error) {
        console.error('Error fetching industries:', error);
      }
    };

    if (open) {
      fetchIndustries();
    }
  }, [open]);

  const handleIndustryChange = (event: any) => {
    const selectedIndustry = industries.find(ind => ind.id === event.target.value);
    if (selectedIndustry) {
      setIndustryId(selectedIndustry.id);
      setIndustry(selectedIndustry.name);
    }
  };

  const handleContentChange = (index: number, value: string) => {
    const newContent = [...content];
    newContent[index] = value;
    setContent(newContent);
  };

  const addContentParagraph = () => {
    setContent([...content, '']);
  };

  const removeContentParagraph = (index: number) => {
    const newContent = content.filter((_, i) => i !== index);
    setContent(newContent);
  };

  const handleKeyPointChange = (index: number, value: string) => {
    const newKeyPoints = [...keyPoints];
    newKeyPoints[index] = value;
    setKeyPoints(newKeyPoints);
  };

  const addKeyPoint = () => {
    setKeyPoints([...keyPoints, '']);
  };

  const removeKeyPoint = (index: number) => {
    const newKeyPoints = keyPoints.filter((_, i) => i !== index);
    setKeyPoints(newKeyPoints);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = async () => {
    // Validation
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!summary.trim()) {
      setError('Summary is required');
      return;
    }
    if (!industryId) {
      setError('Industry is required');
      return;
    }
    const validContent = content.filter(p => p.trim());
    if (validContent.length === 0) {
      setError('At least one content paragraph is required');
      return;
    }
    const validKeyPoints = keyPoints.filter(kp => kp.trim());
    if (validKeyPoints.length === 0) {
      setError('At least one key point is required');
      return;
    }
    if (!conclusion.trim()) {
      setError('Conclusion is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const articleData = {
        title,
        summary,
        industry,
        industryId,
        content: validContent,
        keyPoints: validKeyPoints,
        conclusion,
        tags,
      };

      if (type === 'article') {
        await articleApi.createArticle(articleData);
      } else {
        await articleApi.createInnovation(articleData);
      }

      // Reset form
      setTitle('');
      setSummary('');
      setIndustry('');
      setIndustryId('');
      setContent(['']);
      setKeyPoints(['']);
      setConclusion('');
      setTags([]);
      setTagInput('');

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error: any) {
      console.error('Error submitting article:', error);
      setError(error.response?.data?.error?.message || 'Failed to submit article');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Submit New {type === 'article' ? 'Article' : 'Innovation Spotlight'}
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          Your {type} will be reviewed by administrators before being published.
        </Alert>

        <TextField
          fullWidth
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          required
          disabled={loading}
        />

        <TextField
          fullWidth
          label="Summary"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          margin="normal"
          multiline
          rows={3}
          required
          disabled={loading}
        />

        <FormControl fullWidth margin="normal" required>
          <InputLabel>Industry</InputLabel>
          <Select
            value={industryId}
            onChange={handleIndustryChange}
            label="Industry"
            disabled={loading}
          >
            {industries.map((ind) => (
              <MenuItem key={ind.id} value={ind.id}>
                {ind.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ mt: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Content Paragraphs *
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addContentParagraph}
              disabled={loading}
            >
              Add Paragraph
            </Button>
          </Box>

          {content.map((paragraph, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label={`Paragraph ${index + 1}`}
                value={paragraph}
                onChange={(e) => handleContentChange(index, e.target.value)}
                multiline
                rows={4}
                disabled={loading}
              />
              {content.length > 1 && (
                <IconButton
                  onClick={() => removeContentParagraph(index)}
                  disabled={loading}
                  color="error"
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Key Points *
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addKeyPoint}
              disabled={loading}
            >
              Add Key Point
            </Button>
          </Box>

          {keyPoints.map((point, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label={`Key Point ${index + 1}`}
                value={point}
                onChange={(e) => handleKeyPointChange(index, e.target.value)}
                disabled={loading}
              />
              {keyPoints.length > 1 && (
                <IconButton
                  onClick={() => removeKeyPoint(index)}
                  disabled={loading}
                  color="error"
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>

        <TextField
          fullWidth
          label="Conclusion"
          value={conclusion}
          onChange={(e) => setConclusion(e.target.value)}
          margin="normal"
          multiline
          rows={3}
          required
          disabled={loading}
        />

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Tags
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              label="Add Tag"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              disabled={loading}
            />
            <Button onClick={handleAddTag} variant="outlined" disabled={loading}>
              Add
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => handleRemoveTag(tag)}
                disabled={loading}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Submit for Review'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArticleForm;
